import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import Input from '../../components/common/Input';
import Label from '../../components/common/Label';
import ErrorMessage from '../../components/common/ErrorMessage';

/**
 * PasswordInput Component
 *
 * Renders a password input field with a toggle button to show or hide the password.
 * Includes a label, an input that switches type between "password" and "text" based on the visibility state,
 * and an error message display for validation feedback.
 *
 * Props:
 * - value: Current value of the password input.
 * - onChange: Callback for handling input changes.
 * - error: Error message string for validation.
 * - errorRef: Ref for the error message element (for accessibility).
 * - visible: Boolean flag indicating if the password is visible.
 * - onToggleVisibility: Callback function to toggle the password visibility.
 *
 * Uses FontAwesome icons for the eye/eye-slash toggle button and Tailwind CSS classes for styling.
 */
const PasswordInput = React.forwardRef(
  ({ value, onChange, error, errorRef, visible, onToggleVisibility }, ref) => (
    <div className="w-full">
      <Label variant="dark" htmlFor="password" customClass="block text-sm font-normal">
        Password
      </Label>
      <div className="relative">
        <Input
          type={visible ? 'text' : 'password'}
          id="password"
          value={value}
          onChange={onChange}
          placeholder="Enter Your Password"
          variant="tertiary"
          ref={ref}
        />
        <button
          type="button"
          onClick={onToggleVisibility}
          className="absolute right-3 bottom-2 bg-none border-none cursor-pointer w-auto"
          aria-label={visible ? 'Hide password' : 'Show password'}
        >
          <FontAwesomeIcon icon={visible ? faEyeSlash : faEye} className="text-lg text-gray-500" />
        </button>
      </div>
      <ErrorMessage message={error} className="!static mt-2" ref={errorRef} />
    </div>
  ),
);

export default PasswordInput;
